<template>
  <checkout-section-card>
    <h2
      class="flex items-center gap-2.5 text-xl font-semibold uppercase tracking-wide text-mouse"
    >
      <fa-icon :icon="faSliders" class="size-5 text-rhino" aria-hidden="true" />
      {{ unitTypeLabel }}
      <span
        v-if="hasServerDelimiter"
        ref="infoIconRef"
        class="relative"
        @mouseenter="tooltipVisible = true"
        @mouseleave="tooltipVisible = false"
        @focusin="tooltipVisible = true"
        @focusout="tooltipVisible = false"
      >
        <fa-icon
          :icon="faCircleInfo"
          class="size-4 cursor-help transition-colors"
          :class="tooltipVisible ? 'text-brand' : 'text-rhino/60'"
          aria-hidden="true"
        />
        <teleport to="body">
          <span
            v-if="tooltipVisible"
            class="pointer-events-none fixed z-9999 w-64 -translate-x-1/2 -translate-y-full rounded-lg border border-depth-3 bg-depth-0 px-3 py-2 text-xs font-normal normal-case tracking-normal text-rhino shadow-lg"
            :style="{
              top: `${infoIconBounds.top.value - 8}px`,
              left: `${infoIconBounds.left.value + infoIconBounds.width.value / 2}px`,
            }"
          >
            {{
              t('checkout/configure/units/capacity_tooltip', {
                maxServer: maxServerUnits,
              })
            }}
          </span>
        </teleport>
      </span>
    </h2>

    <!-- Skeleton mirrors the real layout so swapping in content doesn't resize the card -->
    <div
      v-if="checkoutStore.isLoading && !checkoutStore.units"
      class="animate-pulse"
    >
      <div class="mx-auto h-9 w-32 rounded-lg bg-depth-2"></div>
      <div class="mt-6 h-4 w-full rounded-full bg-depth-2"></div>
    </div>

    <!-- A failed fetch also leaves units empty -->
    <p v-else-if="!hasUnitValues && !checkoutStore.error" class="text-rhino">
      {{ t('checkout/configure/units/empty') }}
    </p>
    <p v-else-if="!hasUnitValues" class="text-danger-fg">
      {{ t('checkout/error/order_info') }}
    </p>

    <!-- Fixed size — single unit count, no selector. Count + unit are highlighted. -->
    <i18n-t
      v-else-if="hasFixedUnits"
      :keypath="fixedKeypath"
      tag="p"
      class="text-rhino"
    >
      <template #product>{{ fixedProduct }}</template>
      <template #count>
        <span class="font-bold text-brand">{{ fixedCount }}</span>
      </template>
      <template #unit>
        <span class="font-bold text-brand">{{ fixedUnit }}</span>
      </template>
    </i18n-t>

    <!-- Actual content -->
    <template v-else>
      <!-- Value input -->
      <div class="flex items-center justify-center gap-2">
        <label for="checkout-unit-input" class="sr-only">{{
          unitTypeLabel
        }}</label>
        <input
          id="checkout-unit-input"
          :value="displayValue"
          type="number"
          :min="minUnits"
          :max="maxUnits"
          class="w-20 rounded-lg border-2 border-depth-3 bg-depth-0 px-2 py-1 text-center font-bold text-brand focus:border-brand focus:outline-none"
          @change="onManualInput"
        />
        <span class="text-mouse">{{ unitSuffix }}</span>
      </div>

      <!-- Slider -->
      <div ref="sliderContainerRef" class="mt-4">
        <ui-range-slider
          :model-value="displayValue"
          :label="unitTypeLabel"
          :min="minUnits"
          :max="maxUnits"
          :delimiter="maxServerUnits ?? undefined"
          thumb-label
          @update:model-value="onSliderInput"
          @change="onSliderCommit"
        >
          <template #ticks="{ tickLeft }">
            <span
              v-for="(tick, idx) in sliderTicks"
              :key="tick"
              class="absolute text-rhino"
              :class="tickAlignClass(idx, sliderTicks.length)"
              :style="{ left: tickLeft(tick) }"
            >
              {{ tick }}
            </span>
          </template>
        </ui-range-slider>
      </div>

      <!-- Capacity legend -->
      <div
        v-if="hasServerDelimiter"
        class="mt-4 flex flex-wrap items-center gap-x-5 gap-y-1.5 text-sm"
      >
        <span class="flex items-center gap-1.5">
          <span class="inline-block size-2.5 rounded-full bg-brand"></span>
          <span class="text-rhino">
            {{
              t('checkout/configure/units/server_label', {
                max: maxServerUnits,
                unit: unitSuffix,
              })
            }}
          </span>
        </span>
        <span class="flex items-center gap-1.5">
          <span
            class="inline-block size-2.5 rounded-full"
            style="
              background: color-mix(
                in srgb,
                var(--color-brand) 35%,
                var(--color-depth-3)
              );
            "
          ></span>
          <span class="text-rhino">
            {{
              t('checkout/configure/units/cloud_label', {
                max: maxUnits,
                unit: unitSuffix,
              })
            }}
          </span>
        </span>
      </div>
    </template>
  </checkout-section-card>
</template>

<script setup lang="ts">
import { faCircleInfo, faSliders } from '@fortawesome/pro-solid-svg-icons';

const { t } = useI18n();
const checkoutStore = useCheckoutStore();

const draggingValue = ref<number | null>(null);
const sliderContainerRef = ref<HTMLElement>();
const { width: containerWidth } = useElementSize(sliderContainerRef);

const infoIconRef = ref<HTMLElement>();
const tooltipVisible = ref(false);
const infoIconBounds = useElementBounding(infoIconRef);

const unitTypeLabel = computed(() => checkoutStore.unitTypeLabel);
const unitSuffix = computed(() => checkoutStore.unitSuffixLabel);

const hasUnitValues = computed<boolean>(
  () => (checkoutStore.units?.values.length ?? 0) > 0,
);

const minUnits = computed<number>(() => {
  if (!checkoutStore.units || checkoutStore.units.values.length === 0) return 1;
  return Math.min(...checkoutStore.units.values);
});

const maxUnits = computed<number>(() => {
  if (!checkoutStore.units || checkoutStore.units.values.length === 0) return 1;
  return Math.max(...checkoutStore.units.values);
});

const hasFixedUnits = computed<boolean>(() => maxUnits.value <= minUnits.value);

const fixedProduct = computed<string>(
  () => checkoutStore.product?.name || checkoutStore.product?.title || '',
);

const fixedCount = computed<number>(() => minUnits.value);

// getUnitSuffix returns '' for UNSPECIFIED, which would leave a dangling gap mid-sentence.
const fixedUnit = computed<string>(
  () => unitSuffix.value || t('checkout/configure/units/units'),
);

// Without a product name the sentence has no {product} slot, so fall back to the generic copy.
const fixedKeypath = computed<string>(() =>
  fixedProduct.value
    ? 'checkout/configure/units/fixed'
    : 'checkout/configure/units/fixed_generic',
);

const maxServerUnits = computed<number | null>(
  () => checkoutStore.units?.maxServerUnits ?? null,
);

const hasServerDelimiter = computed<boolean>(() => {
  const ms = maxServerUnits.value;
  if (ms == null) return false;
  return ms > minUnits.value && ms < maxUnits.value;
});

const displayValue = computed<number>(() => {
  if (draggingValue.value !== null) return draggingValue.value;
  return checkoutStore.selectedUnits ?? minUnits.value;
});

function tickAlignClass(idx: number, total: number): string {
  if (idx === 0) return 'translate-x-0';
  if (idx === total - 1) return '-translate-x-full';
  return '-translate-x-1/2';
}

const sliderTicks = computed<number[]>(() => {
  const min = minUnits.value;
  const max = maxUnits.value;
  if (max <= min) return [min];

  const range = max - min;
  const w = containerWidth.value || 360;
  const targetSegments = w < 640 ? 4 : 9;
  const rawStep = range / targetSegments;

  const magnitude = 10 ** Math.floor(Math.log10(rawStep));
  const fraction = rawStep / magnitude;
  let niceStep: number;
  if (fraction <= 1.5) niceStep = magnitude;
  else if (fraction <= 3.5) niceStep = 2 * magnitude;
  else if (fraction <= 7.5) niceStep = 5 * magnitude;
  else niceStep = 10 * magnitude;

  let ticks: number[] = [min];
  let v = Math.ceil((min + 1) / niceStep) * niceStep;
  while (v < max) {
    ticks.push(v);
    v += niceStep;
  }
  ticks.push(max);

  // Always show the delimiter value; suppress any regular tick within 4% of it
  const delimiter = maxServerUnits.value;
  if (
    hasServerDelimiter.value &&
    delimiter !== null &&
    !ticks.includes(delimiter)
  ) {
    const delimPct = ((delimiter - min) / range) * 100;
    ticks = ticks.filter(
      (tick) => Math.abs(((tick - min) / range) * 100 - delimPct) >= 4,
    );
    ticks.push(delimiter);
    ticks.sort((a, b) => a - b);
  }

  return ticks;
});

// `default` is optional; without this fallback a fixed-size product has no selector left to set it.
watch(
  () => checkoutStore.units,
  (units) => {
    if (!units || !hasUnitValues.value) return;
    // No selector is rendered when the size is fixed, so a stale/deep-linked value can't be corrected by hand.
    if (hasFixedUnits.value) {
      if (checkoutStore.selectedUnits !== minUnits.value) {
        checkoutStore.selectUnits(minUnits.value);
      }
      return;
    }
    if (checkoutStore.selectedUnits === null) {
      checkoutStore.selectUnits(units.default ?? minUnits.value);
    }
  },
  { immediate: true },
);

function onManualInput(event: Event): void {
  const raw = Number((event.target as HTMLInputElement).value);
  const clamped = Math.min(Math.max(raw, minUnits.value), maxUnits.value);
  draggingValue.value = null;
  checkoutStore.selectUnits(clamped);
}

function onSliderInput(val: number): void {
  draggingValue.value = val;
}

function onSliderCommit(val: number): void {
  draggingValue.value = null;
  checkoutStore.selectUnits(val);
}
</script>
